import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./HomeBestsellersSection.css"; // Using its own CSS file
import { useCart } from "../../Context/CartContext";
import { useWishlist } from "../../Context/WishlistContext";
import { useCurrency } from "../../Context/CurrencyContext";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { getBestsellerProducts } from "../api";

// Constants
const BASE_URL = "https://volumaire.backendapihub.com";
const DEFAULT_IMAGE = "/images/dailyroutine2.webp";

function HomeBestsellersSection() {
  const { addToCart, openCart } = useCart();
  const { addToWishlist, isInWishlist } = useWishlist();
  const { formatPrice } = useCurrency();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [perView, setPerView] = useState(4);
  const [isHovered, setIsHovered] = useState(false);

  // Normalize image URL function
  const normalizeImageUrl = (imageUrl) => {
    if (!imageUrl || imageUrl.trim() === "" || imageUrl === "null" || imageUrl === "undefined") {
      return null;
    }
    
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://') || imageUrl.startsWith('data:')) {
      return imageUrl;
    }
    
    if (imageUrl.startsWith('/')) {
      return `${BASE_URL}${imageUrl}`;
    }
    
    return `${BASE_URL}/${imageUrl}`;
  };

  // Check if image URL is valid
  const isValidImageUrl = (url) => {
    if (!url) return false;
    const strUrl = String(url).trim();
    return strUrl !== "" && 
           strUrl !== "null" && 
           strUrl !== "undefined" && 
           !strUrl.includes('undefined') &&
           !strUrl.includes('null');
  };

  // Get image source (no hover logic, always first image)
  const getImageSource = (apiImage) => {
    try {
      if (isValidImageUrl(apiImage)) {
        return apiImage;
      }
      return DEFAULT_IMAGE;
    } catch (error) {
      console.error("Error in getImageSource:", error);
      return DEFAULT_IMAGE;
    }
  };

  // ========== ROBUST PRICE EXTRACTOR ==========
  const extractPriceValue = (product) => {
    // Helper to parse any value into a number (handles strings with symbols, objects, etc.)
    const parsePrice = (value) => {
      if (value === undefined || value === null) return NaN;
      
      // If it's already a number, return it
      if (typeof value === 'number') return value;
      
      // If it's a string, clean it and parse
      if (typeof value === 'string') {
        // Remove currency symbols, commas, spaces, and keep only digits, dot, minus
        const cleaned = value.replace(/[^0-9.-]+/g, '');
        const parsed = parseFloat(cleaned);
        return isNaN(parsed) ? NaN : parsed;
      }
      
      // If it's an object, look for common price fields
      if (typeof value === 'object') {
        // Check for typical subfields
        if (value.value !== undefined) return parsePrice(value.value);
        if (value.amount !== undefined) return parsePrice(value.amount);
        if (value.USD !== undefined) return parsePrice(value.USD);
        if (value.EUR !== undefined) return parsePrice(value.EUR);
        if (value.INR !== undefined) return parsePrice(value.INR);
        
        // Try to find any field that might contain price
        for (let key in value) {
          if (key.toLowerCase().includes('price') || key.toLowerCase().includes('amount') || key.toLowerCase().includes('sale')) {
            const val = parsePrice(value[key]);
            if (!isNaN(val)) return val;
          }
        }
      }
      
      return NaN;
    };

    // Priority list of fields to check in order
    const priceFields = [
      'discountedPrice', 'salePrice', 'price', 'productPrice', 
      'regularPrice', 'listPrice', 'unitPrice', 'amount', 'value'
    ];
    
    // Check each field in order
    for (let field of priceFields) {
      if (product[field] !== undefined && product[field] !== null) {
        const parsed = parsePrice(product[field]);
        if (!isNaN(parsed)) return parsed;
      }
    }

    // If product has variants, use the first variant's price
    if (product.variants && Array.isArray(product.variants) && product.variants.length > 0) {
      for (let variant of product.variants) {
        const parsed = parsePrice(variant.price);
        if (!isNaN(parsed)) return parsed;
      }
    }

    // Fallback to 0 if nothing found
    return 0;
  };
  // ============================================

  // Fetch bestseller products from API
  useEffect(() => {
    const fetchBestsellers = async () => {
      try {
        setLoading(true);
        const response = await getBestsellerProducts();
        
        // Check if response has data property or is an array directly
        const productsData = response.data || response || [];
        
        // Map API response to match our product structure
        const mappedProducts = productsData.map(product => {
          const normalized = { ...product };
          
          // Ensure we have an id
          normalized.id = normalized._id || normalized.id || `product-${Math.random().toString(36).substr(2, 9)}`;
          
          // Set basic product info
          normalized.brand = normalized.brand || "MOKOSH";
          normalized.name = normalized.productName || normalized.name || "Product Name";
          normalized.title = normalized.productName || normalized.name || "Product Name";
          
          // Extract price using robust helper
          const priceValue = extractPriceValue(product);
          normalized.priceValue = priceValue;
          normalized.price = priceValue; // Keep backward compatibility
          
          // Handle images exactly like ProductList
          let img1 = "";
          let img2 = "";
          const imagesArray = [];
          
          if (normalized.images && Array.isArray(normalized.images)) {
            if (normalized.images.length >= 2) {
              img1 = typeof normalized.images[0] === 'string' ? normalized.images[0] : normalized.images[0]?.url || "";
              img2 = typeof normalized.images[1] === 'string' ? normalized.images[1] : normalized.images[1]?.url || "";
            } else if (normalized.images.length === 1) {
              img1 = typeof normalized.images[0] === 'string' ? normalized.images[0] : normalized.images[0]?.url || "";
              img2 = img1;
            }
          } else if (normalized.image) {
            if (typeof normalized.image === 'string') {
              img1 = normalized.image;
              img2 = normalized.image;
            } else if (normalized.image.url) {
              img1 = normalized.image.url;
              img2 = normalized.image.url;
            }
          } else if (normalized.productImage) {
            img1 = typeof normalized.productImage === 'string' ? normalized.productImage : normalized.productImage?.url || "";
            img2 = img1;
          }
          
          // Handle image1 and image2 fields if present
          if (normalized.image1 && typeof normalized.image1 === 'string') {
            if (!img1) img1 = normalized.image1;
            if (!img2) img2 = normalized.image1;
          }
          
          if (normalized.image2 && typeof normalized.image2 === 'string') {
            if (img1 && !img2) img2 = normalized.image2;
          }
          
          // Normalize and validate image URLs
          if (isValidImageUrl(img1)) {
            const normalizedImg1 = normalizeImageUrl(img1);
            if (normalizedImg1) {
              img1 = normalizedImg1;
              imagesArray.push(img1);
            }
          }
          
          if (isValidImageUrl(img2)) {
            const normalizedImg2 = normalizeImageUrl(img2);
            if (normalizedImg2) {
              img2 = normalizedImg2;
              if (!imagesArray.includes(img2)) imagesArray.push(img2);
            }
          }
          
          // Handle additional images
          if (normalized.image3) {
            const img3 = typeof normalized.image3 === 'string' ? normalized.image3 : normalized.image3?.url || "";
            if (isValidImageUrl(img3)) {
              const normalizedImg3 = normalizeImageUrl(img3);
              if (normalizedImg3 && !imagesArray.includes(normalizedImg3)) {
                imagesArray.push(normalizedImg3);
              }
            }
          }
          
          // Set final image properties
          if (imagesArray.length === 0) {
            normalized.images = [DEFAULT_IMAGE];
            normalized.img1 = DEFAULT_IMAGE;
            normalized.img2 = DEFAULT_IMAGE;
          } else {
            normalized.images = imagesArray;
            normalized.img1 = getImageSource(img1);
            normalized.img2 = getImageSource(img2); // Keep but not used for hover anymore
          }
          
          // Set other properties
          normalized.isNew = normalized.isNew || false;
          normalized.inStock = normalized.inStock !== false;
          
          return normalized;
        });
        
        setProducts(mappedProducts);
        setError(null);
      } catch (err) {
        console.error("Error fetching bestsellers:", err);
        setError("Failed to load bestsellers. Please try again.");
        // Fallback to sample data if API fails
        setProducts([
          { 
            id: 1, 
            brand: "MOKOSH", 
            name: "Firming face serum Orange", 
            title: "Firming face serum Orange",
            price: 599, 
            priceValue: 599, 
            img1: "/images/bestsellersimage1.webp",
            img2: "/images/bestsellersimage1.webp",
            images: ["/images/bestsellersimage1.webp"],
            isNew: true,
            inStock: true
          },
          { 
            id: 2, 
            brand: "MOKOSH", 
            name: "Body salt scrub 300 g", 
            title: "Body salt scrub 300 g",
            price: 699, 
            priceValue: 699, 
            img1: "/images/bestsellersimage2.webp",
            img2: "/images/bestsellersimage2.webp",
            images: ["/images/bestsellersimage2.webp"],
            isNew: false,
            inStock: true
          },
          { 
            id: 3, 
            brand: "MOKOSH", 
            name: "Moisturizing hand lotion", 
            title: "Moisturizing hand lotion",
            price: 799, 
            priceValue: 799, 
            img1: "/images/bestsellersimage3.webp",
            img2: "/images/bestsellersimage3.webp",
            images: ["/images/bestsellersimage3.webp"],
            isNew: true,
            inStock: true
          },
          { 
            id: 4, 
            brand: "MOKOSH", 
            name: "Corrective eye cream", 
            title: "Corrective eye cream",
            price: 899, 
            priceValue: 899, 
            img1: "/images/bestsellersimage4.webp",
            img2: "/images/bestsellersimage4.webp",
            images: ["/images/bestsellersimage4.webp"],
            isNew: true,
            inStock: true
          },
          { 
            id: 5, 
            brand: "MOKOSH", 
            name: "Nourishing Face Oil", 
            title: "Nourishing Face Oil",
            price: 999, 
            priceValue: 999, 
            img1: "/images/bestsellersimage5 (1).webp",
            img2: "/images/bestsellersimage5 (1).webp",
            images: ["/images/bestsellersimage5 (1).webp"],
            isNew: false,
            inStock: true
          },
          { 
            id: 6, 
            brand: "MOKOSH", 
            name: "Body Butter 200ml", 
            title: "Body Butter 200ml",
            price: 555, 
            priceValue: 555, 
            img1: "/images/bestsellersimage6.webp",
            img2: "/images/bestsellersimage6.webp",
            images: ["/images/bestsellersimage6.webp"],
            isNew: false,
            inStock: true
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchBestsellers();
  }, []);

  // Responsive slider setup
  useEffect(() => {
    const resize = () => {
      if (window.innerWidth < 600) setPerView(1);
      else if (window.innerWidth < 1024) setPerView(2);
      else setPerView(4);
      setCurrentSlide(0);
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const maxSlide = Math.max(0, products.length - perView);

  const handleNext = () => {
    if (currentSlide < maxSlide) setCurrentSlide(prev => prev + 1);
  };

  const handlePrev = () => {
    if (currentSlide > 0) setCurrentSlide(prev => prev - 1);
  };

  const handleAddToCart = (product) => {
    const cartItem = {
      ...product,
      uniqueId: `${product.id}-default-default`,
      id: product.id,
      productId: product.id,
      inStock: product.inStock || true,
      selectedSize: "default",
      selectedFragrance: "default",
      qty: 1,
      price: product.priceValue || 0,
      priceValue: product.priceValue || 0,
      name: product.name,
      title: product.title || product.name,
      image: product.img1 || product.images?.[0] || DEFAULT_IMAGE,
      brand: product.brand,
      // Store base price in USD
      basePrice: product.priceValue || 0,
      baseCurrency: 'USD'
    };
    
    addToCart(cartItem, 1);
    openCart();
  };

  const handleWishlistClick = (product, e) => {
    e.stopPropagation();
    
    const wishlistProduct = {
      id: product.id,
      name: product.name,
      title: product.title || product.name,
      price: product.priceValue,
      image: product.img1 || product.images?.[0] || DEFAULT_IMAGE,
      brand: product.brand,
      inStock: product.inStock || true
    };
    
    addToWishlist(wishlistProduct);
  };

  const progressWidth = maxSlide > 0 ? ((currentSlide + 1) / (maxSlide + 1)) * 100 : 100;

  return (
    <section className="bestsellers-section-six">
      <div className="container-six">
        <h2 className="section-title-six">Our Bestsellers</h2>

        {/* Loading state */}
        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading bestsellers...</p>
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div className="error-container">
            <p className="error-message">{error}</p>
          </div>
        )}

        {/* Products slider - only show when not loading and have products */}
        {!loading && products.length > 0 && (
          <div
            className="bestsellers-slider-container-six"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {currentSlide > 0 && (
              <button className="arrow-btn-six arrow-left-six" onClick={handlePrev}>
                <FaChevronLeft />
              </button>
            )}

            {currentSlide < maxSlide && (
              <button className="arrow-btn-six arrow-right-six" onClick={handleNext}>
                <FaChevronRight />
              </button>
            )}

            <div
              className="bestsellers-slider-six"
              style={{
                transform: `translateX(-${currentSlide * (100 / perView)}%)`
              }}
            >
              {products.map((p, index) => {
                // Main image only (no hover effect)
                const mainImage = getImageSource(p.img1);
                
                return (
                  <div 
                    className="product-card-slide-six" 
                    key={p.id}
                  >
                    <div className="product-image-container-six">
                      {/* Show New badge based on product.isNew or index 2,3 as fallback */}
                      {(p.isNew || index === 2 || index === 3) && (
                        <Link to={`/product/${p.id}`} className="new-badge-six">
                          New
                        </Link>
                      )}

                      <button 
                        className={`wishlist-btn-bestseller ${isInWishlist(p.id) ? 'active' : ''}`}
                        onClick={(e) => handleWishlistClick(p, e)}
                        title={isInWishlist(p.id) ? "Remove from wishlist" : "Add to wishlist"}
                      >
                        <svg 
                          width="20" 
                          height="20" 
                          viewBox="0 0 24 24" 
                          fill={isInWishlist(p.id) ? "red" : "none"} 
                          stroke="currentColor" 
                          strokeWidth="2"
                        >
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                        </svg>
                      </button>

                      <Link to={`/product/${p.id}`}>
                        <img 
                          src={mainImage} 
                          alt={p.name} 
                          className="product-img-six" 
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = DEFAULT_IMAGE;
                          }}
                        />
                      </Link>

                      <button
                        className="add-to-cart-btn-six"
                        onClick={() => handleAddToCart(p)}
                      >
                        ADD TO CART
                      </button>
                    </div>

                    <div className="product-details-container-six">
                      <div className="product-details-six">
                        <p className="product-brand-six">{p.brand}</p>
                        <Link to={`/product/${p.id}`} className="product-name-link">
                          <p className="product-name-six">{p.name}</p>
                        </Link>
                        <p className="product-price-six">{formatPrice(p.priceValue)}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="progress-underline-container-six">
              <div 
                className="progress-underline-six"
                style={{ 
                  width: `${progressWidth}%`,
                  transform: `translateX(${currentSlide * (100 / maxSlide)}%)`
                }}
              ></div>
            </div>
          </div>
        )}

        {/* No products message */}
        {!loading && products.length === 0 && !error && (
          <div className="no-products-message">
            <p>No bestsellers found at the moment.</p>
          </div>
        )}

        {/* View All Link with Underline matching text width */}
        <div className="view-all-container-six">
          <Link to="/bestsellers" className="view-all-link-six">
            View All
          </Link>
          <div className="view-all-underline-six"></div>
        </div>
      </div>
    </section>
  );
}

export default HomeBestsellersSection;